import { useState } from 'react';
import { useRequest } from 'ahooks';

import { Box, Heading, InlineFeedback, Paragraph, TextButton } from '@zillow/constellation-10';

import errorMessages from '@common/constants/errorMessages';
import { validateAllInputs } from '../validation';
import faqs from '@common/constants/faqs';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import userPersonas from '@common/constants/userPersonas';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import PhoneVerificationInput from '@rental-manager/components/PhoneVerificationInput';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  confirmVerificationCode: ({ verificationCode }) => Promise<any>;
  currentProperty: ZrmClickstreamListing;
  fromDashboard: boolean;
  onBack: () => void;
  onClose: () => void;
  phoneNumber: string;
  sendVerificationCode: ({ verificationMethod }) => Promise<any>;
  trackValidationError: ({ errors }) => void;
  user: ZrmClickstreamUser;
  verificationMethod: string;
}

const SubmissionPage = (props: Props) => {
  const {
    confirmVerificationCode,
    currentProperty,
    fromDashboard,
    onBack,
    onClose,
    phoneNumber,
    sendVerificationCode,
    trackValidationError,
    user,
    verificationMethod,
  } = props;
  const [verificationCode, setVerificationCode] = useState({
    value: '',
    error: '',
  });
  const [resendStatus, setResendStatus] = useState<string | null>(null);
  const [submitAttempt, setSubmitAttempt] = useState(0);

  const resendVerificationCode = async (): Promise<any> => {
    setResendStatus('init');

    const response = await sendVerificationCode({ verificationMethod });

    if (response && response.error) {
      setResendStatus('fail');
    } else {
      setResendStatus('success');
    }
  };

  const { loading: isLoading, run: onSubmit } = useRequest(
    async () => {
      if (!validateAllInputs(verificationCode, setVerificationCode, trackValidationError)) {
        return;
      }

      const response = await confirmVerificationCode({ verificationCode: verificationCode.value });

      if (response && response.error) {
        setVerificationCode({
          value: verificationCode.value,
          error: response.tooManyAttempts
            ? errorMessages.PHONE_VERIFICATION_TOO_MANY_ATTEMPTS
            : errorMessages.PHONE_VERIFICATION_INCORRECT,
        });
      } else {
        if (!fromDashboard) {
          IreneOnboardingCSEvents.emitContinueFromPhoneVerificationPageEvent({
            currentProperty,
            user,
          });
        }
      }
    },
    {
      manual: true,
      onFinally: () => setSubmitAttempt((attempt) => attempt + 1),
    },
  );

  const renderResendSection = () => {
    if (resendStatus === 'success') {
      return <InlineFeedback tone="success">Code sent</InlineFeedback>;
    } else if (resendStatus === 'fail') {
      return <InlineFeedback tone="critical">We couldn’t resend the code</InlineFeedback>;
    }
    return <TextButton onClick={resendVerificationCode}>Resend code</TextButton>;
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      aside={<FaqComponent title={faqs.PHONE_VERIFICATION.title} faqs={faqs.PHONE_VERIFICATION.body} />}
      onRequestClose={onClose}
      header={fromDashboard ? 'Edit deposit account' : 'Payment setup'}
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          <Heading level={3} textStyle="body-lg-bold">
            Enter your confirmation code
          </Heading>

          <Paragraph>We’ll {verificationMethod === 'sms' ? 'text' : 'call'} you with your code soon.</Paragraph>

          <Paragraph className="fs-exclude">Phone: {phoneNumber}</Paragraph>

          <PhoneVerificationInput
            setVerificationCode={(val) => setVerificationCode({ value: val, error: '' })}
            error={verificationCode.error}
          />
          <Box aria-live="polite">{renderResendSection()}</Box>
        </Box>
      }
      backAction={{ onClick: onBack }}
      footerAriaLabel="Verification code submission actions"
      footerEnd={
        <DebouncedButton resetKey={submitAttempt} loading={isLoading} onClick={onSubmit}>
          Continue
        </DebouncedButton>
      }
    />
  );
};

export default SubmissionPage;
